import * as React from 'react';
import {FormControl, FormHelperText, FormLabel, Box, TextField as TextFieldEl} from '@mui/material';
import { useMemo, memo } from 'react';

interface TextAreaProps {
    variant?: 'outlined' | 'filled' | 'standard';
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    label?: string;
    description?: string;
    required?: boolean;
    value: string;
    error?: string;
    enabled?: boolean;
    visible?: boolean;
    appValidated?: boolean;
}

const TextArea = memo((props: TextAreaProps) => {

    const {
        variant = 'outlined',
        onChange,
        label,
        description,
        required = false,
        value = '',
        error,
        enabled = true,
        visible = true,
        appValidated = false
    } = props;

    const [appHasBeenValidated, setAppHasBeenValidated] = React.useState(false);

    React.useEffect(() => {
        if (appValidated) {
            setAppHasBeenValidated(true);
        }
    }, [appValidated]);

    const showRequiredError = useMemo(() =>
            required && appHasBeenValidated && !value,
        [required, appHasBeenValidated, value]
    );

    const showError = useMemo(() =>
            error !== undefined && error !== "",
        [error, value]
    );

    const hasError = showError || showRequiredError;
    const errorMessage = showRequiredError ? 'This field is required' : error;

    // Memoize computed values
    const showDescription = useMemo(() =>
            description !== undefined && description !== "",
        [description]
    );

    // Memoize sx props to prevent object recreation
    const textFieldSx = useMemo(() => ({
        ".Mui-disabled": { opacity: 0.6 }
    }), []);

    // Early return for hidden components
    if (!visible) {
        return null;
    }

    return (
        <Box sx={{ display: 'block' }}>
            <FormControl className="form-control" fullWidth={true}>
                <FormLabel required={required} color="secondary" className="form-separate-label">
                    {label}
                </FormLabel>
                <TextFieldEl
                    error={hasError}
                    required={required}
                    value={value}
                    onChange={onChange}
                    multiline={true}
                    minRows={5}
                    variant={variant}
                    className='form-control-field'
                    disabled={!enabled}
                    sx={textFieldSx}
                    fullWidth
                />
                {showDescription && (
                    <FormHelperText>
                        {description}
                    </FormHelperText>
                )}
                {hasError && (
                    <FormHelperText error={true}>
                        {errorMessage}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    );
});

TextArea.displayName = 'TextArea';

export default TextArea;

